import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { QuickReply } from '../../types';
import { getQuickRepliesApi, createQuickReplyApi, updateQuickReplyApi, deleteQuickReplyApi } from '../../api/replies';

const QuickReplyManager: React.FC = () => {
  const [replies, setReplies] = useState<QuickReply[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [currentReply, setCurrentReply] = useState<Partial<QuickReply>>({});

  useEffect(() => {
    loadReplies();
  }, []);

  const loadReplies = async () => {
    try {
      const data = await getQuickRepliesApi();
      setReplies(data);
    } catch (error) {
      console.error('Failed to load quick replies', error);
    }
  };

  const handleSave = async () => {
    if (!currentReply.shortcut || !currentReply.content) return;

    try {
      if (currentReply.id) {
        await updateQuickReplyApi(currentReply as QuickReply);
      } else {
        await createQuickReplyApi(currentReply as Omit<QuickReply, 'id'>);
      }
      setIsEditing(false);
      setCurrentReply({});
      loadReplies();
    } catch (error) {
      console.error('Failed to save quick reply', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this quick reply?')) {
      try {
        await deleteQuickReplyApi(id);
        loadReplies();
      } catch (error) {
        console.error('Failed to delete quick reply', error);
      }
    }
  };

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden bg-background">
      <div className="p-6 flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold">快捷回复管理</h2>
            <button
              onClick={() => {
                setCurrentReply({});
                setIsEditing(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-whatsapp-green text-white rounded-md hover:bg-whatsapp-green/90 font-medium transition-colors"
            >
              <Plus className="w-5 h-5" />
              新建
            </button>
          </div>

          {isEditing && (
            <div className="p-6 border border-border rounded-lg bg-card space-y-4 shadow-sm animate-slide-in">
              <div>
                <label className="block text-sm font-medium mb-1.5 text-foreground">
                  快捷键 (输入 / 触发)
                </label>
                <input
                  type="text"
                  value={currentReply.shortcut || ''}
                  onChange={(e) => setCurrentReply({ ...currentReply, shortcut: e.target.value })}
                  className="w-full p-2.5 bg-muted/50 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
                  placeholder="例如: hello"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5 text-foreground">
                  回复内容
                </label>
                <textarea
                    value={currentReply.content || ''}
                    onChange={(e) => setCurrentReply({ ...currentReply, content: e.target.value })}
                    className="w-full p-2.5 bg-muted/50 border border-border rounded-md min-h-[100px] focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all resize-y"
                    placeholder="输入回复内容..."
                  />
                </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={() => setIsEditing(false)}
                  className="px-4 py-2 text-sm font-medium border border-border rounded-md hover:bg-muted transition-colors"
                >
                  取消
                </button>
                <button
                  onClick={handleSave}
                  className="px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                >
                  保存
                </button>
              </div>
            </div>
          )}

          <div className="grid gap-4">
            {replies.map((reply) => (
              <div 
                key={reply.id} 
                className="group flex items-start justify-between p-5 border border-border rounded-lg bg-card hover:border-primary/30 transition-all duration-200 shadow-sm"
              >
                <div className="space-y-2 flex-1 mr-4">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-lg text-accent">/{reply.shortcut}</span>
                  </div>
                  <p className="text-muted-foreground leading-relaxed">{reply.content}</p>
                </div>
                <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 self-start pt-1">
                  <button
                    onClick={() => {
                      setCurrentReply(reply);
                      setIsEditing(true);
                    }}
                    className="p-2 text-muted-foreground hover:text-primary hover:bg-muted rounded-md transition-colors"
                    title="编辑"
                  >
                    <Edit className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(reply.id)}
                    className="p-2 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-md transition-colors"
                    title="删除"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
            {replies.length === 0 && !isEditing && (
              <div className="text-center py-12 border-2 border-dashed border-border rounded-lg">
                <p className="text-muted-foreground text-lg">暂无快捷回复</p>
                <button
                  onClick={() => {
                    setCurrentReply({});
                    setIsEditing(true);
                  }}
                  className="mt-4 text-primary hover:underline"
                >
                  点击创建第一个快捷回复
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickReplyManager;
